import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileText, ImageIcon } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../../shared/api/encounters";
import { externalSlipQueryKeys, externalSlipsApi } from "../../../shared/api/externalSlips";
import { apiClient } from "../../../shared/api/client";
import { describeDose, doseDurationDays } from "../../../shared/dose";
import { formatDate, formatDateTime } from "../../../shared/format";
import type {
  EncounterAttachment,
  EncounterDetail,
  EncounterPrescriptionItem,
  EncounterSummary,
  SoapNote,
} from "../../../shared/types/encounter";
import { SOAP_DEFAULTS } from "../../../shared/types/encounter";
import type { ExternalSlipView } from "../../../shared/types/externalSlip";
import { Skeleton } from "../../../shared/ui/Skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const ENCOUNTER_STATUS_LABEL: Record<string, string> = {
  OPEN: "Đang mở",
  IN_PROGRESS: "Đang thực hiện",
  COMPLETED: "Đã hoàn tất",
  SIGNED: "Hoàn thành",
  AMENDED: "Đã bổ sung",
  VOIDED: "Đã huỷ",
  CANCELLED: "Đã huỷ",
};

/** Đoạn kết quả dài hơn ngưỡng này thì thu gọn, có nút "Xem thêm" — phiếu siêu âm migrate từ SalePro là một khối chữ liền hàng nghìn ký tự. */
const LONG_TEXT = 600;

/** Đoạn văn kết quả: giữ xuống dòng, thu gọn khi quá dài. */
function ResultText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > LONG_TEXT;
  return (
    <div>
      <p className={cn("m-0 whitespace-pre-wrap text-sm", long && !expanded && "line-clamp-6")}>
        {text}
      </p>
      {long && (
        <button
          type="button"
          className="mt-1 text-sm font-medium text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? "Thu gọn" : "Xem thêm"}
        </button>
      )}
    </div>
  );
}

/**
 * Phiếu MeetDoctor lồng bảng trong bảng: `<table><td colspan=2><table>…` — ô ngoài chỉ là khung
 * bố cục (có cả cột rỗng bên phải). Kẻ ô cho cả ô ngoài lẫn bảng trong là ba đường viền sát nhau
 * ở mép phải và đáy ("border chồng chéo", 02/11/2026), nên Ô nào CHỨA bảng thì không viền, không
 * đệm. Chỉ ô đó — không phải mọi ô của bảng chứa nó: phiếu nội soi TMH đặt các hàng "Kết luận /
 * Chẩn Đoán / Lời dặn BS" làm hàng kề bên hàng ôm bảng "Kết quả soi", và tắt viền theo BẢNG làm
 * ba hàng ấy trôi lơ lửng không khung (03/11/2026). Bảng lồng trong ô giữ lề dọc 8px: viền của nó và
 * viền hàng kề bên là hai bảng khác nhau nên không gộp được, sát nhau thì thành một vạch dày và chữ
 * hai bên như đè lên nhau.
 *
 * HTML gốc của phiếu ngoài (kết quả siêu âm / đơn thuốc MeetDoctor), hiện nguyên như SalePro trong
 * một khung viền. HIS đã làm sạch bằng {@code Jsoup.clean} TRƯỚC khi lưu (`ExternalExamSlip`), cùng
 * chỗ và cùng lý do với `PortalSlipPage`; bảng trong phiếu được kẻ ô bằng selector con vì phiếu
 * không mang CSS. Ảnh trong phiếu là URL của hệ thống ngoài, tải thẳng như trên SalePro.
 */
/**
 * Ô bảng trống trong phiếu MeetDoctor (`<td> </td>`, `<td>&nbsp;</td>`, và cả `<th>&nbsp;</th>`) —
 * bố cục gốc dùng chúng làm cột đệm. Kẻ viền cho chúng là vẽ ra một dải ô rỗng bên phải mỗi hàng;
 * `:empty` của CSS lại không khớp ô chỉ chứa khoảng trắng, nên dọn phần ruột trắng trước để
 * selector bắt được. `<th>` phải dọn như `<td>`: phiếu kết quả siêu âm kết hàng "MÔ TẢ KẾT QUẢ"
 * bằng một `<th>&nbsp;</th>` rộng 30px, các hàng dưới không có ô đó, nên nó thành một ô viền thò
 * ra ngoài mép bảng và đè lên viền hàng kề (04/11/2026).
 */
export function normalizeSlipHtml(html: string): string {
  return html.replace(/<(td|th)(\s[^>]*)?>(?:\s|&nbsp;|&#160;)*<\/\1>/gi, "<$1$2></$1>");
}

export function SlipHtml({ html, testId }: { html: string; testId: string }) {
  return (
    <div
      data-testid={testId}
      className="overflow-x-auto rounded-md border border-border bg-muted/20 p-3 text-sm [&_h2]:my-2 [&_h2]:text-base [&_h2]:font-semibold [&_img]:my-1 [&_img]:h-auto [&_img]:max-w-full [&_p]:my-1 [&_table]:my-1 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_td:empty]:border-0 [&_td:empty]:p-0 [&_td:has(table)]:border-0 [&_td:has(table)]:p-0 [&_th:has(table)]:border-0 [&_th:has(table)]:p-0 [&_td>table]:my-2 [&_th>table]:my-2 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th:empty]:border-0 [&_th:empty]:p-0"
      dangerouslySetInnerHTML={{ __html: normalizeSlipHtml(html) }}
    />
  );
}

/** Một ô giá trị dùng trong phần tóm tắt (nhãn nhỏ trên, giá trị dưới). */
function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Nội dung "Kết quả" rút từ bản ghi SOAP — các ô bác sĩ đã viết, bỏ câu mặc định của mẫu trống
 * ("Chưa có dữ kiện cận lâm sàng…") vì đó không phải kết quả. Dữ liệu migrate từ SalePro để phần
 * mô tả siêu âm / nội soi ở `priorClsResults`, lời dặn ở `definitivePlan`.
 */
export function resultSections(soap: SoapNote): { label: string; text: string }[] {
  const out: { label: string; text: string }[] = [];
  const push = (label: string, text: string | null | undefined, skip?: string) => {
    const t = text?.trim();
    if (t && t !== skip) out.push({ label, text: t });
  };
  push("Bệnh sử", soap.history.presentIllness);
  push("Kết quả cận lâm sàng", soap.assessment.priorClsResults, SOAP_DEFAULTS.priorClsResults);
  push("Hướng xử trí", soap.assessment.preliminaryPlan);
  push("Lời dặn", soap.assessment.definitivePlan, SOAP_DEFAULTS.unchanged);
  return out;
}

function doseSummary(it: EncounterPrescriptionItem): string {
  const days = doseDurationDays(it);
  return [describeDose(it), days ? `${days} ngày` : null].filter(Boolean).join(" · ");
}

/**
 * Tải một tệp đính kèm về dạng object URL. Đường `/download` cần JWT (header, không cookie) rồi
 * 302 sang URL presigned, nên `<img src>` thẳng không dùng được — phải đi qua `apiClient`.
 * Trả về null khi chưa tải xong hoặc tải hỏng; URL được thu hồi khi component gỡ.
 */
function useAttachmentObjectUrl(encounterId: string, att: EncounterAttachment, enabled: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let objectUrl: string | null = null;
    apiClient
      .get<Blob>(`/v1/encounters/${encounterId}/attachments/${att.id}/download`, {
        responseType: "blob",
      })
      .then((r) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [encounterId, att.id, enabled]);
  return { url, failed };
}

/** Ô 80px như SalePro: ảnh thì thu nhỏ bấm để phóng to; tệp khác thì biểu tượng + tên, bấm để tải. */
export function AttachmentTile({
  encounterId,
  att,
  onPreview,
}: {
  encounterId: string;
  att: EncounterAttachment;
  onPreview: (att: EncounterAttachment, url: string) => void;
}) {
  const isImage = att.mimeType.startsWith("image/");
  const { url, failed } = useAttachmentObjectUrl(encounterId, att, isImage && att.downloadable);
  const isPdf = att.mimeType === "application/pdf";

  if (isImage) {
    return (
      <button
        type="button"
        title={att.fileName}
        disabled={!url}
        onClick={() => url && onPreview(att, url)}
        data-testid="encounter-attachment"
        className={cn(
          "flex size-20 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40 transition-colors",
          url ? "hover:border-primary" : "cursor-default",
        )}
      >
        {url ? (
          <img src={url} alt={att.fileName} className="h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1 px-1 text-center">
            <ImageIcon
              aria-hidden="true"
              className={cn("size-6", failed ? "text-destructive" : "text-muted-foreground")}
            />
            <span className="w-full truncate text-[10px] leading-tight text-muted-foreground">
              {failed ? "Không tải được" : att.downloadable ? "Đang tải…" : "Chưa sẵn sàng"}
            </span>
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      title={att.fileName}
      disabled={!att.downloadable}
      onClick={() => encountersApi.downloadAttachment(encounterId, att.id, att.fileName)}
      data-testid="encounter-attachment"
      className="flex size-20 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/40 p-1 text-center transition-colors hover:border-primary disabled:cursor-default disabled:opacity-60"
    >
      <FileText
        aria-hidden="true"
        className={cn("size-6", isPdf ? "text-destructive" : "text-primary")}
      />
      <span className="w-full truncate text-[10px] leading-tight text-muted-foreground">
        {att.fileName}
      </span>
    </button>
  );
}

interface Props {
  /** Lịch hẹn đang xem — phiếu ngoài (MeetDoctor) nối theo id này. */
  appointmentId: string;
  /**
   * Lần khám HIS mở từ lịch hẹn, nếu có — vẽ phần tóm tắt ngay, không chờ chi tiết. `null` khi lịch
   * đã hoàn tất mà không có lần khám HIS (lượt khám ghi nhận từ phiếu quét QR): khi đó thẻ chỉ còn
   * phần phiếu ngoài.
   */
  summary: EncounterSummary | null;
}

/**
 * Thẻ "Kết quả & Tài liệu" trên màn Chi tiết đặt lịch — bản HIS của thẻ cùng tên trên SalePro:
 * ảnh/tài liệu đính kèm, chẩn đoán, kết quả (nội dung bác sĩ ghi), đơn thuốc. Đọc
 * `GET /v1/encounters/{id}` (có @PhiAccess ở máy chủ) chỉ khi thẻ này được dựng, tức lịch đã có
 * lần khám. Phần tóm tắt vẽ từ `summary` ngay; chi tiết hỏng thì nói ra và vẫn giữ link mở lần khám.
 */
export function EncounterResultCard({ appointmentId, summary }: Props) {
  const encounterId = summary?.id ?? "";
  const detail = useQuery({
    queryKey: encounterQueryKeys.detail(encounterId),
    queryFn: () => encountersApi.get(encounterId),
    enabled: !!encounterId,
  });
  /*
   * Phiếu ngoài nối vào lịch hẹn này (MeetDoctor qua QR / migrate từ SalePro). SalePro hiện nguyên
   * phiếu — kết quả siêu âm có ảnh, đơn thuốc có tiêu đề phòng khám — nên ở đây cũng vậy: khi có
   * phiếu, phần "Kết quả" và "Đơn thuốc" lấy từ HTML phiếu; bản SOAP đã ép phẳng chỉ dùng khi
   * KHÔNG có phiếu (lần khám gõ trực tiếp trong HIS).
   */
  const slips = useQuery({
    queryKey: externalSlipQueryKeys.forAppointment(appointmentId),
    queryFn: () => externalSlipsApi.forAppointment(appointmentId),
  });
  const [preview, setPreview] = useState<{ att: EncounterAttachment; url: string } | null>(null);

  const d: EncounterDetail | undefined = detail.data;
  const slip: ExternalSlipView | null = slips.data?.[0] ?? null;
  const diagnoses = d?.diagnoses ?? [];
  const sections = d ? resultSections(d.soap) : [];
  const rx = d?.prescription.items ?? [];
  const attachments = d?.attachments ?? [];
  const hasSlipResult = !!slip?.labResultHtml;
  const hasSlipRx = !!slip?.prescriptionHtml;
  /*
   * Dải ảnh thu nhỏ: khi phiếu ngoài đã mang sẵn ảnh trong HTML kết quả (như SalePro hiện), chỉ
   * còn hiện các tệp KHÔNG phải ảnh — ảnh migrate từ chính phiếu đó mà hiện hai lần thì rối. Không
   * có phiếu thì hiện mọi tệp đính kèm của lần khám.
   */
  const stripAttachments = hasSlipResult
    ? attachments.filter((a) => !a.mimeType.startsWith("image/"))
    : attachments;
  const showStrip = !hasSlipResult || stripAttachments.length > 0;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Kết quả &amp; Tài liệu</CardTitle>
        {summary && (
          <Button asChild variant="ghost" size="sm">
            <Link to={`/encounters/${summary.id}`}>
              Mở lần khám
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-4">
        {summary ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KV label="Lần khám" value={ENCOUNTER_STATUS_LABEL[summary.status] ?? summary.status} />
            <KV label="Bác sĩ khám" value={summary.doctorName || "—"} />
            <KV label="Lý do khám" value={summary.chiefComplaint ?? "—"} />
            <KV label="Hoàn tất lúc" value={formatDateTime(summary.completedAt)} />
          </div>
        ) : slip ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KV label="Nguồn" value={slip.source === "MEETDOCTOR" ? "Phiếu MeetDoctor" : slip.source} />
            <KV label="Ngày khám" value={formatDate(slip.examDate)} />
            <KV label="Tái khám" value={formatDate(slip.nextVisitDate)} />
            <KV label="Nhập lúc" value={formatDateTime(slip.importedAt)} />
          </div>
        ) : null}

        <Separator />

        {(summary && detail.isLoading) || slips.isLoading ? (
          <Skeleton height={120} />
        ) : summary && (detail.isError || !d) && !slip ? (
          <>
            <p className="m-0 text-sm text-destructive" role="status">
              Không tải được chi tiết lần khám. Mở lần khám để xem đầy đủ.
            </p>
            {summary.diagnosisSummary && (
              <KV label="Chẩn đoán" value={summary.diagnosisSummary} />
            )}
          </>
        ) : !summary && !slip ? (
          <p className="m-0 text-sm text-muted-foreground">Chưa có kết quả khám cho lịch hẹn này.</p>
        ) : (
          <>
            {/* Ảnh & tài liệu */}
            {showStrip && (
            <div>
              <h3 className="mb-2 mt-0 text-sm font-semibold text-foreground">
                Ảnh &amp; tài liệu
                {stripAttachments.length > 0 && (
                  <span className="ml-1 font-normal text-muted-foreground">({stripAttachments.length})</span>
                )}
              </h3>
              {stripAttachments.length === 0 ? (
                <p className="m-0 text-sm text-muted-foreground">Chưa có ảnh/tài liệu đính kèm.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="encounter-attachments">
                  {stripAttachments.map((att) => (
                    <AttachmentTile
                      key={att.id}
                      encounterId={encounterId}
                      att={att}
                      onPreview={(a, url) => setPreview({ att: a, url })}
                    />
                  ))}
                </div>
              )}
            </div>
            )}

            {showStrip && <Separator />}

            {/* Chẩn đoán — một dòng "Chẩn đoán: …" như SalePro khi chỉ có một câu. */}
            <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <h3 className="m-0 text-sm font-semibold text-primary">Chẩn đoán:</h3>
              {diagnoses.length === 0 ? (
                <p className="m-0 text-sm">
                  {summary?.diagnosisSummary ?? slip?.diagnosis ?? (
                    <span className="text-muted-foreground">Chưa có chẩn đoán.</span>
                  )}
                </p>
              ) : (
                <ul className="m-0 list-none p-0 text-sm" data-testid="encounter-diagnoses">
                  {diagnoses.map((dx) => (
                    <li key={dx.id} className="inline py-0.5 [&:not(:last-child)]:after:content-['_·_']">
                      {dx.icd10Code && <span className="font-mono text-xs text-muted-foreground">{dx.icd10Code} · </span>}
                      <span className={dx.primary ? "font-medium" : undefined}>{dx.label}</span>
                      {dx.note && <span className="text-muted-foreground"> — {dx.note}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Kết quả — HTML phiếu ngoài (như SalePro) khi có; không thì các ô SOAP bác sĩ ghi. */}
            {hasSlipResult && (
              <>
                <Separator />
                <div>
                  <h3 className="mb-2 mt-0 text-sm font-semibold text-primary">Kết quả:</h3>
                  <SlipHtml html={slip!.labResultHtml!} testId="slip-lab-result" />
                </div>
              </>
            )}
            {!hasSlipResult && sections.length > 0 && (
              <>
                <Separator />
                <div className="grid gap-3" data-testid="encounter-result">
                  {sections.map((s) => (
                    <div key={s.label}>
                      <h3 className="mb-1 mt-0 text-sm font-semibold text-primary">{s.label}</h3>
                      <ResultText text={s.text} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Đơn thuốc — HTML phiếu ngoài khi có; không thì bảng thuốc của HIS. */}
            {hasSlipRx && (
              <>
                <Separator />
                <div>
                  <h3 className="mb-2 mt-0 text-sm font-semibold text-primary">Đơn thuốc:</h3>
                  <SlipHtml html={slip!.prescriptionHtml!} testId="slip-prescription" />
                </div>
              </>
            )}
            {!hasSlipRx && d && (
            <>
            <Separator />
            <div>
              <h3 className="mb-2 mt-0 text-sm font-semibold text-primary">
                Đơn thuốc
                {d.prescription.signed && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    đã ký {d.prescription.signedAt ? formatDateTime(d.prescription.signedAt) : ""}
                  </span>
                )}
              </h3>
              {rx.length === 0 ? (
                <p className="m-0 text-sm text-muted-foreground">Không có đơn thuốc.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm" data-testid="encounter-prescription">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Thuốc</th>
                        <th className="py-1 pr-3 font-medium">SL</th>
                        <th className="py-1 font-medium">Cách dùng</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {rx.map((it) => (
                        <tr key={it.id}>
                          <td className="py-1.5 pr-3 font-medium text-foreground">
                            {it.medicineName}
                            {it.medicineStrength ? ` ${it.medicineStrength}` : ""}
                          </td>
                          <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">
                            {it.quantity} {it.medicineUnit}
                          </td>
                          <td className="py-1.5 text-muted-foreground">{doseSummary(it)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate text-base">{preview?.att.fileName}</DialogTitle>
          </DialogHeader>
          {preview && (
            <img
              src={preview.url}
              alt={preview.att.fileName}
              className="max-h-[75vh] w-full rounded-md object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
